import type { JSX } from 'react';
import React from 'react';

/**
 * Feathr brand marks, taken verbatim from the official horizontal lockup
 * (Feathr-Lockup-Horizontal-Midnight.svg). The path data is unmodified; only
 * the viewBoxes differ, cropped to each element's true bounding box so the
 * marks sit flush instead of floating in the source file's padding.
 *
 *   lockup content  35.1 35.1 513 114
 *   symbol only    434.1 35.1 114 114   (square)
 */

interface IMarkProps {
  className?: string;
  /** Defaults to brand midnight; pass a token for muted or accented contexts. */
  fill?: string;
}

const SYMBOL_PATH =
  'M544.02,70.23h-26.87c-2.28,0-4.12-1.85-4.12-4.12v-26.87c0-2.28,1.85-4.12,4.12-4.12h26.87c2.28,0,4.12,1.85,4.12,4.12v26.87c0,2.28-1.85,4.12-4.12,4.12ZM473.76,75.9l-23.67,28.33c-1.26,1.51-1.95,3.42-1.95,5.38v17.87c0,1.48,1.79,2.22,2.83,1.17l23.26-23.26c1.57-1.57,2.46-3.71,2.46-5.94v-22.49c0-1.55-1.94-2.25-2.93-1.06ZM504.9,38.62l-21.08,25.22c-1.26,1.51-1.95,3.42-1.95,5.38v24.51c0,1.48,1.79,2.22,2.83,1.17l20.67-20.67c1.57-1.57,2.46-3.71,2.46-5.94v-28.62c0-1.55-1.94-2.25-2.93-1.06ZM506.29,106.56h-22.49c-2.23,0-4.36.88-5.94,2.46l-23.26,23.26c-1.04,1.04-.3,2.83,1.17,2.83h17.87c1.97,0,3.87-.69,5.38-1.95l28.33-23.67c1.19-.99.49-2.93-1.06-2.93ZM543.56,75.42h-28.62c-2.23,0-4.36.88-5.94,2.46l-20.67,20.67c-1.04,1.04-.3,2.83,1.17,2.83h24.51c1.97,0,3.87-.69,5.38-1.95l25.22-21.08c1.19-.99.49-2.93-1.06-2.93ZM444.98,134.1l-10.31,11.92c-.37.43-.58.98-.58,1.55v.69c0,.48.39.87.87.87h.69c.57,0,1.11-.2,1.54-.57l11.95-10.3c.51-.44.8-1.08.8-1.75v-2.19c0-.56-.46-1.02-1.02-1.02h-2.2c-.67,0-1.31.29-1.75.8Z';

const WORDMARK_PATH =
  'M64.01,59.29v2.5h20v15.55h-20v57.78h-17.78v-57.78h-11.11v-15.55h11.11v-4.17c0-17.64,6.94-22.51,21.67-22.51h16.11v15.55h-12.36c-7.5,0-7.64.56-7.64,8.61h0v.02ZM153.99,102.91c.28-2.08.42-3.47.42-5.42,0-26.66-17.64-36.81-34.59-36.81-22.37,0-36.53,14.86-36.53,37.78s13.06,37.77,36.81,37.77c19.59,0,29.86-11.52,33.06-26.66h-18.06c-2.08,6.52-6.39,11.11-14.86,11.11-11.11,0-18.33-6.67-19.58-17.78h53.34,0ZM136.64,90.68h-35.28c1.8-8.74,6.94-15.55,19.02-15.55,10.27,0,15.55,6.66,16.25,15.55h.01ZM231.01,61.79h-17.78v2.78c0,2.92.69,5.14,1.95,7.64.69,1.39,1.53,3.05.14,3.75s-2.23-1.11-2.5-1.66c-4.02-8.06-11.81-13.61-23.19-13.61-19.02,0-31.94,16.67-31.94,37.77s12.92,37.77,31.94,37.77c11.11,0,18.06-6.11,23.19-13.75.27-.42.83-1.81,2.08-1.39,1.53.56.97,1.95.27,3.33-1.25,2.5-1.95,4.72-1.95,7.5v3.2h17.78V61.78h0ZM193.78,120.12c-12.5,0-18.33-10-18.33-21.67s5.83-21.67,18.33-21.67c11.67,0,19.44,10,19.44,21.67s-7.78,21.67-19.44,21.67ZM265.86,77.34h20v-15.55h-20v-20h-17.78v20h-13.33v15.55h13.33v35.28c0,17.64,6.94,22.5,21.67,22.5h16.11v-15.55h-12.36c-7.5,0-7.64-.69-7.64-8.75v-33.47h0ZM335.27,60.68c-12.77,0-21.11,10.27-24.44,17.64-.56,1.11-.97,1.94-2.08,1.66-1.81-.41-.98-2.36-.42-3.61,1.39-2.92,2.22-5.56,2.22-10v-31.25h-17.78v100h17.78v-29.86c0-16.94,9.44-28.47,19.72-28.47,9.31,0,13.62,4.58,13.62,21.11v37.23h17.78v-43.33c0-20.83-10.55-31.11-26.39-31.11h0ZM384.97,77.48c-1.66-.28-1.25-1.95-.69-3.2,1.25-2.49,2.08-5.27,2.08-8.47v-4.03h-17.78v73.34h17.78v-31.81c0-18.6,5.97-25.97,26.66-24.58v-18.06c-17.92,0-21.95,5.27-26.39,15.42-.14.42-.83,1.53-1.66,1.39h0Z';

export const BRAND_MIDNIGHT = '#19263c';

/** Full horizontal lockup — wordmark plus feather. Used in the page header. */
export function FeathrLockup({
  className,
  fill = BRAND_MIDNIGHT,
}: Readonly<IMarkProps>): JSX.Element {
  return (
    <svg aria-label={'Feathr'} className={className} role={'img'} viewBox={'35.1 35.1 513 114'}>
      <path d={WORDMARK_PATH} fill={fill} />
      <path d={SYMBOL_PATH} fill={fill} />
    </svg>
  );
}

/** Feather symbol alone. Square — render it at equal width and height. */
export function FeathrMark({
  className,
  fill = BRAND_MIDNIGHT,
}: Readonly<IMarkProps>): JSX.Element {
  return (
    <svg
      aria-hidden={true}
      className={className}
      focusable={'false'}
      viewBox={'434.1 35.1 114 114'}
    >
      <path d={SYMBOL_PATH} fill={fill} />
    </svg>
  );
}
